/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { RuleContext } from '@alfresco/adf-extensions';
import { GROUP_TRANSFER_CALENDAR_SITE_COLLABORATOR, GROUP_TRANSFER_CALENDAR_SITE_MANAGER, GROUP_TRANSFER_CALENDAR_SITE_CONTRIBUTOR, UDEU_TRANSFER_TYPE, UDEU_TRANSFER_ACCEPTED_STATUS, UDEU_TRANSFER_SENDED_STATUS, UDEU_TRANSFER_PENDING_STATUS, UDEU_TRANSFER_REJECTED_STATUS, GROUP_TRANSFERS_SITE, GROUP_TRANSFERS_SITE_MANAGER, UDEU_TRANSFER_DRAFT_STATUS, UDEU_STATUS_PROP, UDEU_TRANSFER_CALENDAR_TYPE, UDEU_TRANSFER_CATALOGED_STATUS } from '../model/contants/deusto';
import { hasPropertyValue, isAllowedOperation, isInGroup, isType, isTypeViewer } from 'projects/alden-extension/src/lib/rules/alden.rules';
import { NodeEntry, Person } from '@alfresco/js-api';
import { DELETE_ALLOWABLE_OPERATION, UPDATE_ALLOWABLE_OPERATION } from 'projects/alden-extension/src/lib/models/alden-constants';
import { isMyElement } from './udeu.rules';

export function isTransferCalendarViewer(context: RuleContext): boolean {
  return isTypeViewer(context, 'transfer-calendar');
}

export function isTransfersViewer(context: RuleContext): boolean {
  return isTypeViewer(context, 'transfers');
}

export function canCreateTransferCalendar(context: RuleContext): boolean {
  const profile = context.profile;
  return isInGroup(profile, GROUP_TRANSFER_CALENDAR_SITE_MANAGER) || isInGroup(profile, GROUP_TRANSFER_CALENDAR_SITE_COLLABORATOR) || isInGroup(profile, GROUP_TRANSFER_CALENDAR_SITE_CONTRIBUTOR);
}

export function canCreateTransfer(context: RuleContext): boolean {
  const profile = context.profile;
  return isInGroup(profile, GROUP_TRANSFERS_SITE);
}

export function canViewCalendarTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isType(nodes[0], UDEU_TRANSFER_CALENDAR_TYPE)) {
    return true;
  }
  return false; 
}

export function canEditCalendarTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && calendarTransferAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION)) {
    return true;
  }
  return false; 
}

export function canDeleteCalendarTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count > 0 && nodes.every((node) => calendarTransferAllowedOperation(node, DELETE_ALLOWABLE_OPERATION))) {
    return true;
  }
  return false; 
}

export function canEditTransfer(context: RuleContext, personDetails: Person): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && transferAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isMyElement(nodes[0], personDetails) && (hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_PENDING_STATUS) 
        || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_DRAFT_STATUS) 
        || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_REJECTED_STATUS))) {
    return true;
  }
  return false;
}

export function canValidateTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && transferAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_TRANSFERS_SITE_MANAGER) && hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_SENDED_STATUS)) {
    return true;
  }
  return false; 
}

export function canCatalogTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && transferAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_TRANSFERS_SITE_MANAGER) && hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_ACCEPTED_STATUS)) {
    return true;
  }
  return false; 
}

export function canEditCatalogTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && transferAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_TRANSFERS_SITE_MANAGER) && hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_CATALOGED_STATUS)) {
    return true;
  }
  return false; 
}

export function canViewTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isType(nodes[0], UDEU_TRANSFER_TYPE)) {
    return true;
  }
  return false; 
}

export function canUnlockEditTransfer(context: RuleContext, personDetails: Person): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && transferAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isMyElement(nodes[0], personDetails) && hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_TRANSFER_SENDED_STATUS)) {
    return true;
  }
  return false; 
}

export function canDeleteTransfer(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  const profile = context.profile;
  if(selection.count > 0 && nodes.every((node) => transferAllowedOperation(node, DELETE_ALLOWABLE_OPERATION))
      && isInGroup(profile, GROUP_TRANSFERS_SITE_MANAGER)) {
    return true;
  }
  return false; 
}

function calendarTransferAllowedOperation(node: NodeEntry, allowableOperation: string): boolean {
  return isType(node, UDEU_TRANSFER_CALENDAR_TYPE) && isAllowedOperation(node, allowableOperation);
}

function transferAllowedOperation(node: NodeEntry, allowableOperation: string): boolean {
  return isType(node, UDEU_TRANSFER_TYPE) && isAllowedOperation(node, allowableOperation);
}